import {
    ref,
    get,
    set,
    update,
    remove,
    query,
    runTransaction,
    onValue,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    onDisconnect,
} from 'firebase/database';
import { useFirebase } from './useFirebase';
import { watch } from 'vue';
import { useAuthentication } from 'ihunter-pathfinder-components';

/*
 * Pathfinder version of FirebaseRepository wrapper, similar to the one in ihunter_services/common.
 *
 * Warning: Calls to the Firebase RealtimeDB must be done from the Pathfinder project.
 * Mysterious issues occur when attempting to do so from the Common project, e.g. HUNT-1778
 *
 */

const watchers = {};

const WatchType = {
    OnValue: 'OnValue',
    ChildAdded: 'ChildAdded',
    ChildChanged: 'ChildChanged',
    ChildRemoved: 'ChildRemoved',
};

let initialized = false; // Ensure we only initialize the watchers once

export function useFirebaseDatabase() {
    const { database } = useFirebase();
    const { user } = useAuthentication();

    const databaseRef = (path) => {
        return ref(database, path);
    };

    const initialize = () => {
        if (!initialized) {
            watch(user, async () => {
                if (!user.value) {
                    unwatchAllPaths();
                }
            });
            initialized = true;
            console.log(`Initialized Firebase Database`);
        }
    };

    /**
     * Creates a watcher entry, which is tracked in `watchers[path][type]` once the Firebase listener is attached.
     * The entry lets callbacks identify their own listener, so an error from a stale/replaced listener
     * can't remove the currently tracked one.
     */
    const createWatcherEntry = () => ({ unsubscribe: null, cancelled: false });

    const initializeWatcher = (entry, path, type) => {
        if (entry.cancelled) {
            // The listener was cancelled synchronously while being attached; don't track a dead watcher
            console.debug(`initializeWatcher - ${path} (${type}) was cancelled before it could be tracked`);
            return;
        }
        if (isWatching(path, type)) {
            // Replace the existing watcher. The new listener must be tracked, otherwise it's orphaned:
            // still active in Firebase, but impossible to unsubscribe and invisible to isWatching().
            console.warn(`initializeWatcher - replacing existing watcher ${path} (${type})`);
            unwatchPathForType(path, type);
        } else {
            console.debug(`initializeWatcher ${path} (${type})`);
        }
        watchers[path] = watchers[path] || {};
        watchers[path][type] = entry;
    };

    const isWatching = (path, type) => {
        return type ? Boolean(watchers[path]?.[type]) : Boolean(watchers[path]);
    };

    const unwatchAllPaths = () => {
        for (const path of Object.keys(watchers)) {
            unwatchPath(path);
        }
    };

    const unwatchPath = (path) => {
        const types = watchers[path] ? Object.keys(watchers[path]) : [];
        for (const type of types) {
            unwatchPathForType(path, type);
        }
        delete watchers[path];
    };

    /**
     * Unsubscribes the watcher for the path & type.
     * @param {string} path
     * @param {string} type
     * @param {Object} [entry] - if provided, only unwatch when this is the currently tracked watcher
     */
    const unwatchPathForType = (path, type, entry) => {
        const tracked = watchers[path]?.[type];
        if (!tracked || (entry && tracked !== entry)) {
            return;
        }

        // Remove from tracking *before* unsubscribing: Firebase may synchronously raise queued events
        // (e.g. another listener's cancel callback) while unsubscribing, which can re-enter this function.
        delete watchers[path][type];
        if (Object.keys(watchers[path]).length === 0) {
            delete watchers[path];
        }

        console.debug(`Unwatch ${type} ${path}`);
        try {
            tracked.unsubscribe?.();
        } catch (error) {
            console.warn(`Failed to unwatch ${type} ${path}`, error);
        }
    };

    /**
     * Invokes a caller's callback, logging (rather than throwing) any errors so they don't surface
     * as "FIREBASE WARNING: Exception was thrown by user callback".
     */
    const invokeCallback = (callbackFn, path, type, ...args) => {
        try {
            const result = callbackFn?.(...args);
            if (result instanceof Promise) {
                result.catch((error) => console.error(`Callback failed for ${type} ${path}`, error));
            }
        } catch (error) {
            console.error(`Callback failed for ${type} ${path}`, error);
        }
    };

    const getData = async (path) => {
        let snapshot = await get(databaseRef(path));
        return snapshot.exists() ? snapshot.val() : null;
    };

    const countChildrenForDbPath = async (path) => {
        let snapshot = await get(databaseRef(path));
        return snapshot.exists() ? snapshot.size : 0;
    };

    const updateData = async (path, data) => {
        console.debug(`updateData ${path}`);
        return await update(databaseRef(path), data);
    };

    const bulkUpdateData = async (data) => {
        console.debug(`bulkUpdateData`, Object.keys(data));
        return await update(databaseRef(), data);
    };

    const setData = async (path, data) => {
        console.debug(`setData ${path}`);
        return await set(databaseRef(path), data);
    };

    /**
     * Atomically reads and writes the data at path: fnUpdate(currentValue) returns the new value, or undefined to abort.
     * The first attempt uses the locally cached value (null if the path isn't cached, eg. not under a watched path),
     * so aborting on null only reliably means "doesn't exist" for watched paths. Resolves to { committed, snapshot }.
     *
     * When to use it:
     * - When the new value depends on the current value and another client (another tab, device or user) could write to the same path at the same time, eg. counters, toggles, or "only set if empty". If two clients use getData + setData, one write can overwrite the other. A transaction retries until it commits against the latest value.
     * - When a write should only go ahead if the data is in a certain state, eg. don't recreate an item that was deleted. Return undefined from fnUpdate to cancel the write.
     *
     * When not to use it:
     * - Plain writes that don't depend on the current value: use setData / updateData.
     * - Writes to several paths: use bulkUpdateData. A transaction only covers the one path.
     * - Large paths: a transaction reads and rewrites the whole value at path, so point it at the smallest node that needs to change.
     *
     * Notes:
     * - fnUpdate can run more than once and must have no side effects. It must return the new value, or undefined to cancel.
     * - The first attempt uses the local cache. If the path isn't watched, currentValue is null on the first try even when data exists, so "null means it doesn't exist" only holds for watched paths. Otherwise handle null by returning a value, not by cancelling.
     * - Resolves to { committed, snapshot }. Check committed to see whether the write went through.
     */
    const transactionData = async (path, fnUpdate) => {
        console.debug(`transactionData ${path}`);
        return await runTransaction(databaseRef(path), fnUpdate);
    };

    const removeData = async (path) => {
        console.debug(`removeData ${path}`);
        return await remove(databaseRef(path));
    };

    const watchData = async (path, notify, errorFn) => {
        const type = WatchType.OnValue;
        const entry = createWatcherEntry();
        try {
            entry.unsubscribe = onValue(
                query(databaseRef(path)),
                (snapshot) => {
                    invokeCallback(notify, path, type, snapshot.exists() ? snapshot.val() : null);
                },
                (error) => {
                    handleError(error, path, type, errorFn, entry);
                },
            );
            initializeWatcher(entry, path, type);
        } catch (error) {
            handleError(error, path, type, errorFn, entry);
        }
    };

    const onDataAdded = (path, notify, errorFn) => {
        const type = WatchType.ChildAdded;
        const entry = createWatcherEntry();
        try {
            entry.unsubscribe = onChildAdded(
                query(databaseRef(path)),
                (snapshot) => {
                    invokeCallback(notify, path, type, snapshot);
                },
                (error) => {
                    handleError(error, path, type, errorFn, entry);
                },
            );
            initializeWatcher(entry, path, type);
        } catch (error) {
            handleError(error, path, type, errorFn, entry);
        }
    };

    const onDataChanged = (path, notify, errorFn) => {
        const type = WatchType.ChildChanged;
        const entry = createWatcherEntry();
        try {
            entry.unsubscribe = onChildChanged(
                query(databaseRef(path)),
                (snapshot, prevChildKey) => {
                    invokeCallback(notify, path, type, snapshot, prevChildKey);
                },
                (error) => {
                    handleError(error, path, type, errorFn, entry);
                },
            );
            initializeWatcher(entry, path, type);
        } catch (error) {
            handleError(error, path, type, errorFn, entry);
        }
    };

    const onDataRemoved = (path, notify, errorFn) => {
        const type = WatchType.ChildRemoved;
        const entry = createWatcherEntry();
        try {
            entry.unsubscribe = onChildRemoved(
                query(databaseRef(path)),
                (snapshot) => {
                    invokeCallback(notify, path, type, snapshot);
                },
                (error) => {
                    handleError(error, path, type, errorFn, entry);
                },
            );
            initializeWatcher(entry, path, type);
        } catch (error) {
            handleError(error, path, type, errorFn, entry);
        }
    };

    const disconnectSet = (path, data) => {
        return onDisconnect(databaseRef(path)).set(data);
    };

    const disconnectUpdate = (path, data) => {
        return onDisconnect(databaseRef(path)).update(data);
    };

    const disconnectRemove = (path) => {
        return onDisconnect(databaseRef(path)).remove();
    };

    const disconnectCancel = (path) => {
        return onDisconnect(databaseRef(path)).cancel();
    };

    const handleError = (error, path, type, errorFn, entry) => {
        // The listener is cancelled by Firebase and no longer active.
        // Only untrack it if it's still the tracked watcher (it may already have been replaced or unwatched).
        if (entry) {
            entry.cancelled = true;
        }
        unwatchPathForType(path, type, entry);

        if (error?.code !== 'PERMISSION_DENIED') {
            console.error(`Error watching ${type} for path ${path}`, error);
        }

        invokeCallback(errorFn, path, type);
    };

    initialize();

    return {
        getData,
        countChildrenForDbPath,
        setData,
        updateData,
        bulkUpdateData,
        removeData,
        transactionData,
        watchData,
        onDataAdded,
        onDataChanged,
        onDataRemoved,
        unwatchPath,
        unwatchPathForType,
        isWatching,

        disconnectSet,
        disconnectUpdate,
        disconnectRemove,
        disconnectCancel,

        WatchType,
    };
}
